import React, {useEffect, useState} from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormLabel from '@mui/material/FormLabel';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';

type WaitTime = {
    amount: number,
    type: 'hours' | 'days' | 'weeks'
}

const TIME_MAP = {
    week: 60 * 24 * 7,
    day: 60 * 24,
    hour: 60,
}

const getTime = (amount: number, type: WaitTime["type"]): number => {
    switch (type) {
        case "weeks": return amount * TIME_MAP.week
        case "days": return amount * TIME_MAP.day
        case "hours": return amount * TIME_MAP.hour
    }
}

type WaitTimeInputProps = {
    initialValue: number,
    onChange: (nextValue: number) => void,
}

export const WaitTimeInput = (props: WaitTimeInputProps) => {
    const {initialValue, onChange} = props

    const [waitTime, setWaitTime] = useState<WaitTime>({amount: 0, type: 'hours'})

    const onAmountChange = (value: string) => {
        const formattedValue = Number(value)

        if (isNaN(formattedValue) || formattedValue < 0) return

        setWaitTime(oldState => ({...oldState, amount: formattedValue}))
        onChange(getTime(formattedValue, waitTime.type))
    }

    const onTypeChange = (value: WaitTime['type']) => {
        setWaitTime(oldState => ({...oldState, type: value}))
        onChange(getTime(waitTime.amount, value))
    }

    useEffect(() => {
        if (initialValue === 0) return

        if (initialValue % TIME_MAP.week === 0) {
            setWaitTime({amount: initialValue / TIME_MAP.week, type: 'weeks'})
        } else if (initialValue % TIME_MAP.day === 0) {
            setWaitTime({amount: initialValue / TIME_MAP.day, type: 'days'})
        } else {
            setWaitTime({amount: initialValue / TIME_MAP.hour, type: 'hours'})
        }
    }, [])

    return (
        <Stack gap="1.5rem">
            <FormControl>
                <FormLabel id="waiting-period-label">Wait time</FormLabel>
                <Stack direction="row" gap="0.5rem 1.5rem" flexWrap="wrap">
                    <TextField
                        aria-labelledby="waiting-period-label"
                        onChange={(e) => onAmountChange(e.target.value)}
                        value={waitTime.amount === 0 ? '' : waitTime.amount.toString()}
                        variant="outlined"
                    />
                    <RadioGroup
                        row
                        aria-labelledby="waiting-period-label"
                        value={waitTime.type}
                        onChange={(e) => {
                            switch (e.target.value) {
                                case 'hours':
                                case 'days':
                                case 'weeks': onTypeChange(e.target.value); break;
                            }
                        }}
                    >
                        <FormControlLabel value="hours" control={<Radio />} label="Hours" />
                        <FormControlLabel value="days" control={<Radio />} label="Days" />
                        <FormControlLabel value="weeks" control={<Radio />} label="Weeks" />
                    </RadioGroup>
                </Stack>
            </FormControl>
        </Stack>
    )
}
